import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';

export interface ValidationResultOk<T> {
  value: T,
  errorMsg: null,
}

export interface ValidationResultError {
  errorMsg: string,
}

export type ValidationResult<T> =
  ValidationResultOk<T> |
  ValidationResultError;

export interface ValidationError {
  type: 'validation_error',
  errorMsg: string,
}

export function validateOptionalProperty(o: unknown, key: string): unknown | undefined {
  if (!isObject(o)) {
    throw 'Not an object';
  }
  if (key in o) {
    return (o as Record<string, unknown>)[key];
  } else {
    return undefined;
  }
}

export function validateProperty(o: unknown, key: string): unknown {
  const value = validateOptionalProperty(o, key);
  if (value === undefined) {
    throw `Missing "${key}" property`;
  }
  return value;
}


export function validateConstantProperty<T extends string | boolean | number>(o: unknown, key: string, constantValue: T): T {
  const value = validateOptionalProperty(o, key);
  if (value === undefined) {
    throw `Missing "${key}" property`;
  }

  if (value !== constantValue) {
    throw `"${key}" does not have the expected value ${JSON.stringify(constantValue)}`;
  }

  return constantValue;
}


export function validateNumberProperty(object: unknown, key: string): number {
  const value = validateProperty(object, key);
  if (typeof value !== 'number') {
    throw `"${key}" is not a number`;
  }
  return value;
}

export function validateIntegerProperty(object: unknown, key: string): number {
  const value = validateProperty(object, key);
  if (typeof value !== 'number') {
    throw `"${key}" is not a number`;
  }
  if (!Number.isSafeInteger(value)) {
    throw `"${key}" is not a safe integer: ${value}`;
  }
  return value;
}

export function validateNumberOrNullProperty(object: unknown, key: string): number | null {
  const value = validateOptionalProperty(object, key);
  if (value === null) {
    return null;
  }
  if (typeof value !== 'number') {
    throw `"${key}" is not a number or null`;
  }
  return value;
}

export function validateStringProperty(object: unknown, key: string): string {
  const value = validateProperty(object, key);
  if (typeof value !== 'string') {
    throw `"${key}" is not a string`;
  }
  return value;
}

export function validateTypedStringProperty<T extends string>(
  o: unknown,
  key: string,
  values: readonly T[]
): T {
  const value = validateProperty(o, key);
  if (!values.includes(value as any)) {
    throw `"${key}" is not one of [${values.join(', ')}]`;
  }
  return value as T;
}

export function validateStringOrNullProperty(object: unknown, key: string): string | null {
  const value = validateProperty(object, key);
  if (value === null) {
    return null;
  }
  if (typeof value !== 'string') {
    throw `"${key}" is not a number`;
  }
  return value;
}

export function validateOptionalStringProperty(object: unknown, key: string): string | undefined {
  const value = validateOptionalProperty(object, key);
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== 'string') {
    throw `"${key}" is not a string`;
  }
  return value;
}

export function validateOptionalStringOrNullProperty(object: unknown, key: string): string | null | undefined {
  const value = validateOptionalProperty(object, key);
  if (value === undefined || value === null) {
    return value;
  }
  if (typeof value !== 'string') {
    throw `"${key}" is not a string`;
  }
  return value;
}

export function validateOptionalNumberProperty(object: unknown, key: string): number | undefined {
  const value = validateOptionalProperty(object, key);
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== 'number') {
    throw `"${key}" is not a number`;
  }
  return value;
}

export function validateBooleanProperty(object: unknown, key: string): boolean {
  const value = validateProperty(object, key);
  if (typeof value !== 'boolean') {
    throw `"${key}" is not a boolean`;
  }
  return value;
}

export function validateOptionalBooleanProperty(object: unknown, key: string): boolean | undefined {
  const value = validateOptionalProperty(object, key);
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== 'boolean') {
    throw `"${key}" is not a boolean`;
  }
  return value;
}

export function validateOrThrow<T>(
  object: unknown,
  validationFunction: ValidationFunction<T>,
): T {
  const result = validationFunction(object);
  if (result.errorMsg !== null) {
    throw result.errorMsg;
  }
  return result.value;
}

export function validateObjectProperty<T>(
  o: unknown,
  key: string,
  typeName: string,
  validationFunction: ValidationFunction<T>,
): T {
  const result = validationFunction(validateProperty(o, key));
  if (result.errorMsg !== null) {
    throw `"${key}" is not of type ${typeName}: ${result.errorMsg}`;
  }
  return result.value;
}

export function validateOptionalObjectProperty<T>(
  o: unknown,
  key: string,
  typeName: string,
  validationFunction: ValidationFunction<T>,
): T | undefined {
  const value = validateOptionalProperty(o, key);
  if (value === undefined) {
    return undefined;
  }
  const result = validationFunction(value);
  if (result.errorMsg !== null) {
    throw `"${key}" is not of type ${typeName}: ${result.errorMsg}`;
  }
  return result.value;
}

export function validateObjectOrNullProperty<T>(
  o: unknown,
  key: string,
  typeName: string,
  validationFunction: ValidationFunction<T>,
): T | null {
  const value = validateProperty(o, key);
  if (value === null) {
    return null;
  }
  const result = validationFunction(value);
  if (result.errorMsg !== null) {
    throw `"${key}" is not of type ${typeName} or null: ${result.errorMsg}`;
  }
  return result.value;
}

export function validateOptionalArrayProperty<T>(
  o: unknown,
  key: string,
  typeName: string,
  validationFunction: ValidationFunction<T>,
): T[] | undefined {
  const value = validateOptionalProperty(o, key);
  if (value === undefined) {
    return undefined;
  }
  try {
    return validateObjectArray(value, typeName, validationFunction);
  } catch (error) {
    if (typeof error === 'string') {
      throw `"${key}" is not of type array of ${typeName}: ${error}`;
    } else {
      throw error;
    }
  }
}



function validateNumber(u: unknown): ValidationResult<number> {
  if (typeof u === 'number') {
    return { value: u, errorMsg: null};
  }

  return {
    errorMsg: `${JSON.stringify(u)} is not a number`,
  };
}


export function validateNumberArray(
  object: unknown,
  key: string,
): number[] {
  return validateArrayProperty(object, key, 'number', validateNumber);
}


export function validateArrayProperty<T>(
  object: unknown,
  key: string,
  typeName: string,
  validationFunction: ValidationFunction<T>,
): T[] {
  const value = validateOptionalArrayProperty(object, key, typeName, validationFunction);
  if (value === undefined) {
    throw `Missing "${key}" property`;
  }
  return value;
}

export function validateObjectArray<T>(
  object: unknown,
  typeName: string,
  validationFunction: ValidationFunction<T>
): T[] {
  if (!isArray(object)) {
    throw 'Not an array';
  }
  const result : T[] = [];
  for (const element of object) {
    const validationResult = validationFunction(element);
    if (validationResult.errorMsg !== null) {
      throw `One of the array entries is not of type ${typeName}: ${validationResult.errorMsg}`;
    }
    result.push(validationResult.value);
  }
  return result;
}


export function validateRecordStringToObject<T>(
  object: unknown,
  typeName: string,
  validationFunction: ValidationFunction<T>
): Record<string, T> {
  if (!isObject(object)) {
    throw 'Not an object';
  }
  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(object)) {
    const validationResult = validationFunction(value);
    if (validationResult.errorMsg !== null) {
      throw `One of the object values is not of type ${typeName}: ${validationResult.errorMsg}`;
    }
    result[key] = validationResult.value;
  }
  return result;
}

export function validateOptionalRecordStringToObject<T>(
  object: unknown,
  typeName: string,
  validationFunction: ValidationFunction<T>
): Record<string, T> | undefined {
  if (object === undefined) {
    return undefined;
  }
  return validateRecordStringToObject(object, typeName, validationFunction);
}

export function makeValidationFunction<T>(inner: (u: unknown) => T): ValidationFunction<T> {
  return (u: unknown) => {
    try {
      return { value: inner(u), errorMsg: null };
    } catch (error: any) {
      if (typeof error === 'string') {
        return { errorMsg: error };
      }
      throw error;
    }
  };
}

export type ValidationFunction<T> = (u: unknown) => ValidationResult<T>;

export function stringValidator(u: unknown): ValidationResult<string>  {
  if (typeof u !== 'string') {
    return { errorMsg: 'Not a string' };
  }
  return { value: u, errorMsg: null };
}

export function stringOrNullValidator(u: unknown): ValidationResult<string | null>  {
  if (u === null) {
    return {
      value: null,
      errorMsg: null,
    }
  }
  if (typeof u !== 'string') {
    return { errorMsg: `Not a string (it is: ${JSON.stringify(u)})` };
  }
  return { value: u, errorMsg: null };
}

export function allowNull<T>(inner: ValidationFunction<T>): ValidationFunction<T | null> {
  return (u: unknown) => {
    if (u === null) {
      return { value: null, errorMsg: null };
    }
    return inner(u);
  };
}
